import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import { getAccessToken } from '~/services/localStorage/auth';
import Loader from '~/ui/components/common/Loader';

import transparentSquare from '~/ui/assets/images/transparentSquare.svg';
import fetchWithAuthentication from './helper';

import styles from './PrivateImage.module.scss';

interface IProps {
  src: string;
  height?: number;
  alt?: string;
  className?: string;
  width?: string | number;
  withLoader?: boolean;
  additionalIcon?: ReactElement;
  disabled?: boolean;
}

const PrivateImage = ({
  src,
  height,
  alt = '',
  className,
  width,
  withLoader,
  additionalIcon,
  disabled = false,
}: IProps): ReactElement => {
  const imageEl = useRef<HTMLImageElement>();

  const [loading, setLoading] = useState(false);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const fetchImage = useCallback(async () => {
    // Prevent blinking when fetching image (1 px image)
    if (imageEl.current) {
      imageEl.current.src = transparentSquare;
    }

    try {
      if (withLoader) setLoading(true);
      const accessToken = getAccessToken();

      const response = await fetchWithAuthentication(src, accessToken);
      if (response.ok) {
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (imageEl.current) {
          imageEl.current.src = objectUrl;
        }
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    }
    setLoading(false);
  }, [showError, src, withLoader]);

  useEffect(() => {
    fetchImage();
  }, [fetchImage]);

  if (additionalIcon) {
    return (
      <>
        {loading && <Loader size={20} className={styles.loader} />}
        <div className={styles.additionalIconContainer}>
          <img
            ref={imageEl}
            alt={alt}
            height={height}
            width={width}
            className={classNames(className, { [styles.disabled]: disabled })}
          />
          <div className={styles.additionalIcon}>{additionalIcon}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {loading && <Loader size={20} className={styles.loader} />}
      <img ref={imageEl} alt={alt} height={height} width={width} className={className} />
    </>
  );
};

export default PrivateImage;
